/**
 * Platform detection & payment routing rules for Selah Downloads.
 *
 * The same React bundle is served as a PWA that can be wrapped for
 *  – Apple App Store (via PWABuilder → iOS wrapper)
 *  – Google Play (TWA)
 *  – Microsoft Store (Hosted App)
 *  – Plain web
 *
 * Apple's App Review Guideline 3.1.1 forbids advertising external payment
 * methods inside an iOS build. So the paywall CTA must be different per
 * platform:
 *
 *   iOS      → hide inline Stripe checkout, show a small "Subscribe on
 *              selahdownloads.com" text link (opens Safari). Eventually
 *              swap to StoreKit IAP when we do a native wrap.
 *   Android  → Stripe web checkout (subject to change if we later use
 *              Play Billing to hit the 15% tier at Google's fee).
 *   Windows  → Stripe web checkout (Microsoft charges 0% for web payments,
 *              vs 15% for native IAP → Stripe is the cheapest path).
 *   Web      → Stripe web checkout (Stripe only takes ~2.9% + $0.30).
 *
 * The `REACT_APP_PLATFORM_TARGET` env variable is the authoritative override
 * — set it at build time when producing per-store bundles via PWABuilder.
 * When unset, we do runtime user-agent sniffing as a fallback so the same
 * bundle still behaves correctly for testers.
 */

const BUILD_TARGET = (process.env.REACT_APP_PLATFORM_TARGET || '').toLowerCase().trim();

/**
 * Read a URL query-string override (?_platform=ios|web|android|windows).
 * Highest-priority mechanism — intended for manual testing and for the
 * PWABuilder iOS wrapper (which can append ?_platform=ios to the URL it
 * loads inside WKWebView).
 */
function urlPlatformOverride() {
  if (typeof window === 'undefined') return '';
  try {
    const q = new URLSearchParams(window.location.search).get('_platform');
    const val = (q || '').toLowerCase().trim();
    return ['ios', 'android', 'windows', 'web'].includes(val) ? val : '';
  } catch (_e) {
    return '';
  }
}

function detectRuntimePlatform() {
  if (typeof navigator === 'undefined') return 'web';
  const ua = (navigator.userAgent || '').toLowerCase();

  // ═══════════════════════════════════════════════════════════════════════
  // HIGHEST-PRIORITY iOS SIGNAL #0: localStorage "iOS memory".
  // ═══════════════════════════════════════════════════════════════════════
  // Once ANY subsequent signal has classified this device as iOS in a
  // previous session, we persist a "yes, iOS" flag to localStorage. That
  // flag then trumps all later re-detection attempts. This is essential
  // because after a purchase we call `window.location.reload()` — and if
  // the URL query param `?_platform=ios` gets stripped by React Router
  // during navigation, AND the UA marker isn't caught in this reload, we
  // could silently degrade back to "web" mode inside the App Store build.
  // That in turn would show the wrong price ($7.77 vs $7.79) and route
  // the user to Stripe on any subsequent Subscribe tap — a hard-fail
  // App Review 3.1.1 violation. The memory flag guarantees stability.
  try {
    if (
      typeof window !== 'undefined' &&
      window.localStorage &&
      window.localStorage.getItem('__selah_platform_memory') === 'ios'
    ) {
      return 'ios';
    }
  } catch (_e) {
    /* localStorage disabled or blocked — fall through to other signals */
  }

  // ═══════════════════════════════════════════════════════════════════════
  // HIGHEST-PRIORITY iOS SIGNAL #1: PWABuilder WKWebView UA marker.
  // ═══════════════════════════════════════════════════════════════════════
  // The native iOS wrapper in WebView.swift explicitly sets a custom User
  // Agent that ends with "PWAShell". This is a synchronous, always-present
  // signal — it does not depend on the JS bridge being registered yet,
  // does not depend on URL query params surviving React Router navigation,
  // and does not depend on WKWebView reporting `standalone: true` (which
  // it does not by default). If we see PWAShell in the UA we are 100%
  // certain we are inside the native App Store build.
  //
  // Without this check, users who signed up on iOS were bounced from the
  // Subscribe button into a Stripe redirect that WKWebView refused
  // (checkout.stripe.com is not in WKAppBoundDomains), producing a blank
  // connection-error screen — see bug reported Aug 14 2026.
  //
  // We also match a broader suffix pattern in case older PWABuilder
  // templates used "PWAShell", "PWAS", "PWAWrapper" or a similar marker.
  if (
    ua.includes('pwashell') ||
    ua.includes('pwawrapper') ||
    /\bpwas\b/.test(ua) ||
    ua.endsWith('pwas')
  ) {
    rememberIOS();
    return 'ios';
  }

  // ═══════════════════════════════════════════════════════════════════════
  // HIGHEST-PRIORITY iOS SIGNAL #2: the native Swift bridge is registered.
  // ═══════════════════════════════════════════════════════════════════════
  // Belt-and-suspenders with SIGNAL #1: if the PWAShell UA marker were
  // ever stripped by a proxy or an OS update, the JS bridge presence still
  // proves we are running inside the iOS wrapper.
  try {
    if (
      typeof window !== 'undefined' &&
      window.webkit &&
      window.webkit.messageHandlers &&
      typeof window.webkit.messageHandlers.selahIAP !== 'undefined'
    ) {
      rememberIOS();
      return 'ios';
    }
  } catch (_e) {
    /* accessing window.webkit can throw on non-Apple engines; ignore */
  }

  // iOS detection (iPad Pros report Mac in UA — check touch capability too).
  const uaIOS =
    /iphone|ipod/.test(ua) ||
    /ipad/.test(ua) ||
    (ua.includes('mac') && typeof document !== 'undefined' && 'ontouchend' in document);

  // A raw iOS user-agent alone is NOT enough to trigger the Apple-safe
  // silent-lock experience — that would penalise iOS Safari visitors on
  // selahdownloads.com who legitimately need to see pricing to convert.
  // We only treat the runtime as iOS when the app is also running in
  // standalone / display-mode: standalone. Both the PWABuilder iOS wrapper
  // (which loads inside WKWebView in fullscreen) and "Add to Home Screen"
  // installations report standalone; regular Safari browser tabs do not.
  if (uaIOS) {
    const iosStandalone =
      typeof window !== 'undefined' && window.navigator && window.navigator.standalone === true;
    const displayStandalone =
      typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(display-mode: standalone)').matches
        : false;
    if (iosStandalone || displayStandalone) {
      rememberIOS();
      return 'ios';
    }
    // iOS UA but browser tab → fall through to 'web' so pricing/checkout
    // remain visible for conversion.
  }

  if (/android/.test(ua)) return 'android';
  if (/windows nt/.test(ua) || /win64|win32/.test(ua)) return 'windows';
  return 'web';
}

/**
 * Persist an "iOS was detected here at least once" flag in localStorage so
 * subsequent reloads / URL rewrites cannot regress the platform back to
 * "web". See SIGNAL #0 in detectRuntimePlatform().
 *
 * Kept as a no-throw helper — a corrupt localStorage or private-mode
 * quota block must never break the detection path.
 */
function rememberIOS() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('__selah_platform_memory', 'ios');
    }
  } catch (_e) {
    /* ignore */
  }
}

export function getPlatformTarget() {
  // 1. Explicit URL query override always wins (test hook + PWABuilder hint).
  const urlOverride = urlPlatformOverride();
  if (urlOverride) {
    // Persist the fact that this device was launched with an iOS override
    // so that later reloads (which may strip the query param) still
    // classify correctly. See detectRuntimePlatform() SIGNAL #0.
    if (urlOverride === 'ios') rememberIOS();
    return urlOverride;
  }

  // 2. localStorage "iOS memory" — MUST come before BUILD_TARGET check.
  //
  // Rationale (Aug 21 2026 bug): our production bundle ships with
  // BUILD_TARGET='web' baked in because the same bundle serves the web
  // marketing site AND the native App Store iOS wrapper. If we let
  // BUILD_TARGET win here, an iOS session that had correctly detected
  // itself via URL param on initial load — but was then navigated to
  // /dashboard after login (stripping the ?_platform=ios param) — would
  // regress back to 'web' mode on every subsequent isIOS() call. That
  // caused the TrialRibbon to display the web $7.77 price and the Trial
  // Banner Subscribe button to attempt a Stripe redirect inside WKWebView.
  //
  // By promoting the localStorage check ABOVE the BUILD_TARGET check,
  // once ANY iOS signal (URL, PWAShell UA, or Swift bridge presence) has
  // fired for this device, iOS mode is sticky for the lifetime of the
  // browser origin — surviving reloads, deep links, and React Router
  // navigations that strip query parameters.
  try {
    if (
      typeof window !== 'undefined' &&
      window.localStorage &&
      window.localStorage.getItem('__selah_platform_memory') === 'ios'
    ) {
      return 'ios';
    }
  } catch (_e) {
    /* localStorage disabled — fall through */
  }

  // 3. Build-time env var — set when producing per-store bundles. Only
  // trusted when it names a REAL store target ('ios', 'android',
  // 'windows'). A 'web' value is treated as an absent signal so that
  // runtime detection can still promote the session to a store target.
  if (BUILD_TARGET && ['ios', 'android', 'windows'].includes(BUILD_TARGET)) {
    if (BUILD_TARGET === 'ios') rememberIOS();
    return BUILD_TARGET;
  }

  // 4. Runtime detection (standalone-gated for iOS).
  return detectRuntimePlatform();
}

export function isIOS() {
  return getPlatformTarget() === 'ios';
}

export function isAndroid() {
  return getPlatformTarget() === 'android';
}

export function isWindowsStore() {
  return getPlatformTarget() === 'windows';
}

export function isWeb() {
  return getPlatformTarget() === 'web';
}

/**
 * Returns true when this build/runtime is allowed to show inline Stripe
 * checkout UI. On iOS we must not — instead we use native Apple IAP via
 * StoreKit 2 (see utils/iapBridge.js and components/Paywall.jsx).
 */
export function canShowInlineStripeCheckout() {
  return !isIOS();
}

/**
 * Returns true when we should surface the Apple IAP purchase flow.
 * This is the ONLY payment surface allowed inside an iOS build per
 * Apple's App Review Guideline 3.1.1.
 */
export function shouldUseAppleIAP() {
  return isIOS();
}

/**
 * Marketing / paywall copy strings that depend on the platform. Keeping
 * these in one place makes copy auditing trivial.
 *
 * NOTE (App Review 3.1.1): On iOS builds we MUST NOT link to any external
 * purchase option. The iOS variant surfaces the Apple StoreKit purchase
 * button. Pricing is displayed because Apple returns the localised price
 * string from the StoreKit product record — that is Apple-official
 * pricing, not steering to another platform.
 */
export function paywallCopy() {
  const platform = getPlatformTarget();
  if (platform === 'ios') {
    return {
      // iOS uses Apple IAP — pricing comes from StoreKit, not from us.
      cta: 'Start 7-day free trial',
      helper: 'Manage or cancel anytime in Settings.',
      externalUrl: null,
      showInlineStripe: false,
      showAppleIAP: true,
      silentLock: false,
    };
  }
  return {
    cta: 'Subscribe & Continue',
    helper: 'Secure checkout via Stripe. Cancel anytime.',
    externalUrl: null,
    showInlineStripe: true,
    showAppleIAP: false,
    silentLock: false,
  };
}

/**
 * Where the "Manage subscription" button should point per platform.
 * On iOS we MUST NOT open a web billing portal (App Review 3.1.1).
 * Apple's canonical deep-link to the user's own subscriptions surface is
 * https://apps.apple.com/account/subscriptions — safe to link to from
 * within an iOS app.
 */
export function manageSubscriptionTarget() {
  if (isIOS()) {
    return {
      kind: 'ios-native',
      url: 'https://apps.apple.com/account/subscriptions',
      label: 'Manage in Settings',
    };
  }
  return {
    kind: 'stripe-portal',
    url: null, // caller resolves via /api/stripe/portal/session
    label: 'Manage subscription',
  };
}

/**
 * PWA / installed detection — used to decide whether to hide install
 * prompts, tighten copy, etc.
 */
export function isRunningStandalone() {
  if (typeof window === 'undefined') return false;
  const iosStandalone = window.navigator.standalone === true;
  const displayModeStandalone = window.matchMedia
    ? window.matchMedia('(display-mode: standalone)').matches
    : false;
  return iosStandalone || displayModeStandalone;
}

/**
 * Returns the display price (in USD) for the current platform.
 *
 * Apple's price ladder does not include $7.77 — the closest available
 * price is $7.79 (per Apple's $0.10-increment tier system for prices
 * under $10). So we charge $7.79 on iOS via StoreKit IAP, while keeping
 * the covenant-anchored $7.77 on web / Android / Windows via Stripe.
 *
 * This is fully App Review 3.1.1 compliant: Apple only forbids steering
 * users to external cheaper prices from within the iOS app. Different
 * prices per platform are permitted as long as the iOS app does not
 * mention or advertise the web price.
 */
export function getDisplayPriceUSD() {
  return isIOS() ? 7.79 : 7.77;
}

/**
 * Returns true when the "7·7·7 Covenant of Completeness, Order, and Rest"
 * theology anchor should render. It is intentionally hidden on iOS
 * because Apple's tier ladder cannot express $7.77 exactly, so leaving
 * the 7·7·7 scriptural anchor visible next to a "$7.79" price would be
 * inconsistent and confuse users. The full theology remains on web.
 */
export function showCovenantTheology() {
  return !isIOS();
}

// ═══════════════════════════════════════════════════════════════════════
// EARLY INITIALIZATION: Run platform detection immediately on module load
// to ensure localStorage is set before any component renders.
// ═══════════════════════════════════════════════════════════════════════
// This is critical for the TrialRibbon pricing display: when a user lands
// on /login?_platform=ios and then gets redirected to /dashboard (losing
// the URL param), we need the localStorage memory to persist the iOS mode.
if (typeof window !== 'undefined') {
  // Trigger platform detection immediately to set localStorage if needed
  getPlatformTarget();
}
